import React, { useState } from 'react';
import { BaseButton, Checkbox, FilterBottomSheet, useT } from '@repo/common';
import FilterList from './FilterList';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/16px/icon_16px_download.svg';

const PERIOD_OPTIONS = [
  'All time',
  'Last 1 month',
  'Last 3 month',
  'Last 4 month',
];
const STATUS_OPTIONS = ['All statuses', 'Submitted', 'Accepted', 'Need prep'];

// 증명서 발급 기간 — 화면 필터와 같은 문구를 쓰고 서버 파라미터로 변환한다
const CERTIFICATE_PERIOD_OPTIONS = [
  'All time',
  'Last 1 month',
  'Last 3 month',
  'Last 4 month',
];
const CERTIFICATE_PERIOD_VALUE = {
  'All time': 'ALL',
  'Last 1 month': '1M',
  'Last 3 month': '3M',
  'Last 4 month': '4M',
};

const ApplicationsHeaderButton = ({
  onDelete,
  hasSelected,
  selectedTab,
  onFilterChange,
  filterPeriod,
  $isMobile,
  isAllSelected,
  onSelectAll,
  statusFilter = 'All statuses',
  onStatusChange,
  onDownloadCertificate,
  isCertificateDownloading = false,
}) => {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [openSheet, setOpenSheet] = useState(null);
  const [isCertificateOpen, setIsCertificateOpen] = useState(false);
  const isSaved = selectedTab === 'saved';

  const handleCertificateSelect = (option) => {
    setIsCertificateOpen(false);
    setOpenSheet(null);
    onDownloadCertificate?.(CERTIFICATE_PERIOD_VALUE[option] ?? 'ALL');
  };

  // period / status를 같은 모양으로 정의 → 버튼도 시트도 이 배열만 돌면 됨
  const filters = [
    {
      key: 'period',
      options: PERIOD_OPTIONS,
      value: filterPeriod,
      onChange: onFilterChange,
    },
    ...(!isSaved
      ? [
          {
            key: 'status',
            options: STATUS_OPTIONS,
            value: statusFilter,
            onChange: onStatusChange,
          },
        ]
      : []),
  ];

  const activeFilter = filters.find((f) => f.key === openSheet);

  /*
   * 구직활동 증명서 — 지원내역 탭에서만 노출 (저장한 공고 탭에는 해당 없음).
   */
  const showCertificate = !isSaved && Boolean(onDownloadCertificate);

  const certificateButton = (
    <div className='relative shrink-0'>
      <BaseButton
        type='text_basic'
        size='s'
        icon={<DownloadIcon />}
        isDisabled={isCertificateDownloading}
        onClick={(e) => {
          e.stopPropagation();
          if ($isMobile) {
            setOpenSheet('certificate');
          } else {
            setIsCertificateOpen((prev) => !prev);
          }
        }}
      >
        {isCertificateDownloading
          ? t('Preparing...', '발급 중...')
          : t('Certificate', '구직활동 증명서')}
      </BaseButton>

      {!$isMobile && isCertificateOpen && (
        <div className='absolute top-[34px] right-0 z-20'>
          <FilterList
            options={CERTIFICATE_PERIOD_OPTIONS}
            selectedOption={null}
            onSelect={handleCertificateSelect}
            onClose={() => setIsCertificateOpen(false)}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className='flex w-full flex-col'>
      {$isMobile && showCertificate && (
        <div className='flex justify-start pb-2'>{certificateButton}</div>
      )}

      <div className='flex items-center justify-between tabletH:items-start tabletH:justify-normal pb-4 gap-2 relative w-full'>
        {!$isMobile && (
          <BaseButton
            type='text_basic'
            size='s'
            icon={isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
            bgclass='pl-2'
            gap='gap-1'
          >
            {filterPeriod}
          </BaseButton>
        )}

        <div className='flex shrink-0 gap-2'>
          {$isMobile && (
            <div className='flex items-center pr-2 gap-2.5'>
              <Checkbox
                checked={isAllSelected}
                onChange={(e) => onSelectAll?.(e.target.checked)}
              />
            </div>
          )}
          {/* 데스크탑에서는 폭이 넉넉해 Delete 옆에 그대로 둔다 */}
          {!$isMobile && showCertificate && certificateButton}
          <BaseButton
            type='text_basic'
            size='s'
            icon={<DeleteIcon />}
            isDisabled={!hasSelected}
            onClick={onDelete}
          >
            Delete
          </BaseButton>
        </div>

        {/* 데스크탑 드롭다운 */}
        {isOpen && (
          <div className='absolute top-[34px] left-0 z-10'>
            <FilterList
              selectedOption={filterPeriod}
              onSelect={(option) => {
                setIsOpen(false);
                onFilterChange?.(option);
              }}
              onClose={() => setIsOpen(false)}
            />
          </div>
        )}

        {/* 모바일 필터 버튼들 — filters 배열만 돌면 됨 */}
        {$isMobile && (
          <div className='flex min-w-0'>
            {filters.map((f) => (
              <BaseButton
                key={f.key}
                icon={
                  openSheet === f.key ? <ArrowTopIcon /> : <ArrowBottomIcon />
                }
                type='text_basic'
                size='s'
                bgclass='bg-transparent border-none pl-1 pr-2 py-1'
                textclass='truncate'
                onClick={() => setOpenSheet(f.key)}
              >
                {f.value}
              </BaseButton>
            ))}
          </div>
        )}

        {/* 모바일 — 증명서 기간 선택 시트 */}
        {$isMobile && openSheet === 'certificate' && (
          <FilterBottomSheet
            options={CERTIFICATE_PERIOD_OPTIONS}
            selected={null}
            onSelect={handleCertificateSelect}
            onClose={() => setOpenSheet(null)}
          />
        )}

        {/* 모바일 바텀시트 — 삼항 없이 activeFilter만 참조 */}
        {$isMobile && activeFilter && (
          <FilterBottomSheet
            options={activeFilter.options}
            selected={activeFilter.value}
            onSelect={(option) => {
              activeFilter.onChange?.(option);
              setOpenSheet(null);
            }}
            onClose={() => setOpenSheet(null)}
          />
        )}
      </div>
    </div>
  );
};

export default ApplicationsHeaderButton;
